/* ============================================================================
   product-order-grid.js — <order-grid> web component (Figma Product Page §01).

   Each variant row has a −/+ stepper that adds or removes that variant in the
   cart. Clicks update the row's quantity immediately (optimistic UI); the
   actual cart sync is debounced per row, so a burst of rapid clicks results
   in one network call once clicking settles, not one call per click. Cart
   requests are still serialised so overlapping rows can't race each other.
   Per-row totals and a running subtotal update live, and the header cart
   bubble is refreshed from the returned section HTML without a full reload.
   The footer button proceeds to checkout. Quote-only rows are plain links
   (RFQ modal) and ignored.
   ========================================================================== */
const ORDER_GRID_SYNC_DEBOUNCE_MS = 500;
const ORDER_GRID_HYDRATE_RETRY_MS = 400;

class OrderGrid extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-order-row]'));
    this.checkoutBtn = this.querySelector('[data-order-grid-checkout]');
    this.checkoutLabelEl = this.querySelector('[data-order-grid-checkout-label]');
    this.totalItemsEl = this.querySelector('[data-total-items]');
    this.subtotalEl = this.querySelector('[data-subtotal]');
    this.errorEl = this.querySelector('[data-order-grid-error]');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.queue = Promise.resolve();
    // Last quantity known to actually be in the cart, per variant — the
    // rollback baseline if a debounced sync fails, and how a settled sync
    // decides whether there's anything left to send.
    this.syncedQty = new Map();
    this.debounceTimers = new Map();

    this.onClick = this.onClick.bind(this);
    this.onCheckout = this.onCheckout.bind(this);

    this.addEventListener('click', this.onClick);
    if (this.checkoutBtn) this.checkoutBtn.addEventListener('click', this.onCheckout);

    this.recalc();
    // Seed each row from the live cart so the display is accurate and "−" acts
    // on the true quantity. Block interaction until this resolves — otherwise
    // a click made while hydrating races the seed: hydrateFromCart would
    // later overwrite the optimistic value with the pre-click snapshot,
    // silently reverting the click and dropping its sync (it'd look
    // identical to the just-reset baseline, so nothing would be sent).
    // isHydrating is the real guard (checked in onClick, so it holds for
    // keyboard-activated clicks and any other non-pointer trigger too);
    // pointer-events-none on top of it is just the visual/hover affordance.
    this.isHydrating = true;
    this.classList.add('pointer-events-none');
    this.queue = this.hydrateFromCart().finally(() => {
      this.isHydrating = false;
      this.classList.remove('pointer-events-none');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.checkoutBtn) this.checkoutBtn.removeEventListener('click', this.onCheckout);
    this.debounceTimers.forEach((timer) => clearTimeout(timer));
    this.debounceTimers.clear();
  }

  /* ── Interaction ─────────────────────────────────────────────────────── */
  onClick(event) {
    if (this.isHydrating) return;
    const step = event.target.closest('[data-step]');
    if (!step) return;
    const row = step.closest('[data-order-row]');
    const input = row && row.querySelector('[data-qty]');
    const variantId = parseInt(row && row.dataset.variantId, 10);
    if (!input || !variantId) return;

    const delta = parseInt(step.dataset.step, 10) || 0;
    const prev = parseInt(input.value, 10) || 0;
    const next = Math.max(0, prev + delta);
    if (next === prev) return;

    // Optimistic UI — reflect every click immediately.
    input.value = next;
    this.recalc();

    // Debounce the actual cart sync per variant: each click within the
    // window cancels and reschedules, so a burst of clicks fires one
    // request carrying the final settled quantity.
    if (this.debounceTimers.has(variantId)) clearTimeout(this.debounceTimers.get(variantId));
    this.debounceTimers.set(variantId, setTimeout(() => {
      this.debounceTimers.delete(variantId);
      this.enqueue(() => this.syncVariant({ variantId, input }));
    }, ORDER_GRID_SYNC_DEBOUNCE_MS));
  }

  /* Serialise cart requests so overlapping variants can't race each other. */
  enqueue(task) {
    this.queue = this.queue.then(task, task);
    return this.queue;
  }

  // Syncs a single variant's row to whatever quantity is currently showing,
  // read fresh here (not captured at click time) since clicking may have
  // continued during the debounce wait.
  async syncVariant({ variantId, input }) {
    // Without a trustworthy load read every baseline is a guess at 0, which
    // would send an add.js for a variant the cart already holds and stack on
    // top of that line instead of setting it. Re-read first; inputs are left
    // alone because the shopper's clicks own them by now.
    if (this.hydrated === false) await this.hydrateFromCart({ attempts: 1, seedInputs: false });

    const target = this.readQty(input);
    const baseline = this.syncedQty.get(variantId) || 0;
    if (target === baseline) return;

    this.setError('');
    try {
      const data = await this.applyQuantity(variantId, target, baseline);
      const confirmedQty = OrderGrid.qtyInCart(data, variantId);
      this.syncedQty.set(variantId, confirmedQty);
      this.recalc();
      this.updateCart(data);

      // Only adopt the cart's number if the row still shows what we sent. A
      // click landing while this request was in flight has already moved the
      // input, and overwriting it there both undid that click on screen and
      // stranded it: the debounced sync behind it would compare the reverted
      // input against this same freshly-stored baseline, find no difference
      // and send nothing, so the click vanished for good.
      if (this.readQty(input) === target) {
        input.value = confirmedQty;
        this.recalc();
        return;
      }

      // The shopper's newer number owns the input. A pending debounce will
      // reconcile it; if it has already fired, settle it here — still inside
      // this queued task, so the ordering of cart requests holds.
      if (!this.debounceTimers.has(variantId)) {
        await this.syncVariant({ variantId, input });
      }
    } catch (err) {
      // Roll back to the last confirmed quantity if the cart rejected it —
      // but only while the row still shows the value that was rejected, for
      // the same reason the success path checks before writing.
      if (this.readQty(input) === target) {
        input.value = baseline;
        this.recalc();
      }
      this.setError(err.message || 'Unable to update your cart. Please try again.');
    }
  }

  /**
   * Reads a row's quantity input as a number.
   * @param {HTMLInputElement} input - The row's quantity field.
   * @returns {number}
   */
  readQty(input) {
    return parseInt(input.value, 10) || 0;
  }

  /**
   * Sets a variant's cart quantity to an absolute target.
   *
   * change.js is the right call whenever a line already exists: it sets the
   * quantity in either direction, 0 included. What it cannot do is create
   * one — for a variant with nothing in the cart it returns 200 OK and
   * silently does nothing (confirmed against the live API: item_count 0,
   * items []), so add.js has to make the first line. id goes as a string
   * either way; change.js validates it against a line-key pattern and
   * rejects a raw JSON integer.
   *
   * With a trustworthy baseline we know which case we are in and spend one
   * request. Without one we ask the cart rather than guess: change.js first,
   * and if the variant is still absent from the response then there was no
   * line to modify, so add.js creates it. Guessing add.js here would be the
   * damaging way to be wrong — its quantity is relative, so it would stack on
   * top of a line the shopper already had instead of setting it. The extra
   * request is spent only when the baseline is unknown or has gone stale
   * (the cart changed in another tab, say), never on the normal path.
   *
   * @param {number} variantId - Variant being set.
   * @param {number} target - Absolute quantity the row is showing.
   * @param {number} baseline - Last quantity known to be in the cart.
   * @returns {Promise<object>} The cart response to render from.
   */
  async applyQuantity(variantId, target, baseline) {
    if (this.hydrated && baseline === 0) {
      return this.cartRequest('add', { items: [{ id: String(variantId), quantity: target }] });
    }

    const changed = await this.cartRequest('change', { id: String(variantId), quantity: target });
    if (target === 0 || OrderGrid.qtyInCart(changed, variantId) > 0) return changed;
    return this.cartRequest('add', { items: [{ id: String(variantId), quantity: target }] });
  }

  /**
   * Total quantity of a variant in a cart response, summed across every line
   * carrying it — matching how hydrateFromCart seeds the baseline. A variant
   * can occupy more than one line (differing properties, or another app's
   * doing), and reading only the first would report a quantity smaller than
   * the one it gets compared against next time.
   * @param {object} data - Cart or add.js response.
   * @param {number} variantId - Variant to total.
   * @returns {number}
   */
  static qtyInCart(data, variantId) {
    return (data.items || []).reduce(
      (sum, item) => (item.variant_id === variantId ? sum + item.quantity : sum),
      0
    );
  }

  // Read the live cart and seed each row's baseline from it.
  //
  // A failed read is not harmless enough to swallow. Every baseline stays 0
  // while the cart may well hold that variant, and both steppers then misread
  // it: "−" is disabled, so the shopper cannot reduce what they already have,
  // and the first "+" takes the baseline-0 branch into add.js, which is
  // relative — one tap jumps the row to the existing quantity plus one.
  // So retry briefly, and record whether we ever got a trustworthy answer.
  //
  // @param {object} [opts]
  // @param {number} [opts.attempts=2] - Tries before giving up.
  // @param {boolean} [opts.seedInputs=true] - Write quantities into the rows.
  //   False when re-reading mid-interaction, where the shopper's clicks own
  //   the inputs and only the baselines are stale.
  async hydrateFromCart({ attempts = 2, seedInputs = true } = {}) {
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await fetch(this.cartUrl(), { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error(res.status);
        // eslint-disable-next-line no-await-in-loop
        const cart = await res.json();
        const qtyByVariant = new Map();
        (cart.items || []).forEach((item) => {
          qtyByVariant.set(item.variant_id, (qtyByVariant.get(item.variant_id) || 0) + item.quantity);
        });
        this.rows.forEach((row) => {
          const id = parseInt(row.dataset.variantId, 10);
          const input = row.querySelector('[data-qty]');
          const qty = qtyByVariant.get(id) || 0;
          if (seedInputs && input && qtyByVariant.has(id)) input.value = qty;
          this.syncedQty.set(id, qty);
        });
        this.hydrated = true;
        this.setError('');
        if (seedInputs) this.recalc();
        return;
      } catch (e) {
        // eslint-disable-next-line no-await-in-loop
        if (attempt < attempts) await OrderGrid.wait(ORDER_GRID_HYDRATE_RETRY_MS);
      }
    }

    // Still no answer. Say so rather than presenting a confident 0, and leave
    // the flag down so the next sync re-reads before choosing add vs change.
    this.hydrated = false;
    this.setError('We could not load your current cart, so the quantities below may be out of date.');
  }

  static wait(ms) {
    return new Promise((resolve) => { setTimeout(resolve, ms); });
  }

  async onCheckout() {
    // Don't navigate away before we even know the true starting cart state.
    if (this.isHydrating) await this.queue;

    // Flush any debounced syncs still pending so checkout reflects every
    // click made just before navigating, not only whichever rows had
    // already settled.
    if (this.debounceTimers.size > 0) {
      if (this.checkoutBtn) this.checkoutBtn.setAttribute('aria-disabled', 'true');

      this.debounceTimers.forEach((timer, variantId) => {
        clearTimeout(timer);
        const row = this.rows.find((r) => parseInt(r.dataset.variantId, 10) === variantId);
        const input = row && row.querySelector('[data-qty]');
        if (input) this.enqueue(() => this.syncVariant({ variantId, input }));
      });
      this.debounceTimers.clear();

      await this.queue;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = `${root}checkout`.replace(/\/+/g, '/');
  }

  /* ── Totals ──────────────────────────────────────────────────────────── */
  recalc() {
    let items = 0;
    let subtotal = 0;
    this.rows.forEach((row) => {
      const price = parseInt(row.dataset.price, 10) || 0;
      const qty = parseInt(row.querySelector('[data-qty]')?.value, 10) || 0;
      const rowTotal = price * qty;
      const totalEl = row.querySelector('[data-row-total]');
      if (totalEl) {
        totalEl.textContent = this.money(rowTotal);
        totalEl.classList.toggle('is-nonzero', rowTotal > 0);
      }
      row.classList.toggle('is-active', qty > 0);
      const minus = row.querySelector('[data-step="-1"]');
      if (minus) minus.disabled = qty <= 0;
      items += qty;
      subtotal += rowTotal;
    });

    if (this.totalItemsEl) this.totalItemsEl.textContent = items;
    if (this.subtotalEl) this.subtotalEl.textContent = this.money(subtotal);

    // Figma "button-green-solid-cart": Empty (#CFD6DC/white) before any
    // quantity is selected, Added (#0FF280/#103150) once items > 0.
    if (this.checkoutBtn) this.checkoutBtn.classList.toggle('is-empty', items === 0);

    // Button always navigates straight to checkout (items are already synced
    // to the cart per click as they're selected), so the label stays static
    // rather than switching to "Add N items to cart".
    if (this.checkoutLabelEl) {
      this.checkoutLabelEl.textContent = this.checkoutLabelEl.dataset.defaultLabel;
    }
  }

  /* ── Cart sync ───────────────────────────────────────────────────────── */
  async cartRequest(action, payload) {
    const res = await fetch(this.cartRoute(action), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...payload, sections: this.cartSections() }),
    });
    const data = await res.json();
    if (!res.ok) {
      throw new Error(data.description || data.message || 'Unable to update your cart.');
    }
    return data;
  }

  // Sections to request so the drawer body and the header bubble both refresh.
  cartSections() {
    const sections = ['cart-icon-bubble'];
    const wrapper = this.cartDrawer && this.cartDrawer.closest('.shopify-section');
    if (wrapper) sections.unshift(wrapper.id.replace('shopify-section-', ''));
    return sections.join(',');
  }

  updateCart(data) {
    // Reuse the theme's own drawer renderer (updates the drawer body + the icon
    // bubble). Keep the drawer closed — the shopper may still be adding sizes.
    if (this.cartDrawer && typeof this.cartDrawer.renderContents === 'function') {
      this.cartDrawer.renderContents(data, false);
      return;
    }
    this.refreshCart(data);
  }

  refreshCart(data) {
    // Fallback for pages without a cart drawer: update the header bubble only.
    const html = data.sections && data.sections['cart-icon-bubble'];
    if (!html) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const fresh = parsed.querySelector('.shopify-section') || parsed.body.firstElementChild;
    document.querySelectorAll('#cart-icon-bubble, [id^="cart-icon-bubble"]').forEach((node) => {
      const replacement = fresh && fresh.querySelector(`#${node.id}`);
      if (replacement) node.innerHTML = replacement.innerHTML;
      else if (fresh) node.innerHTML = fresh.innerHTML;
    });
  }

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  cartRoute(action) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart/${action}.js`.replace(/\/+/g, '/');
  }

  cartUrl() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return `${root}cart.js`.replace(/\/+/g, '/');
  }

  money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      const fmt = (window.theme && window.theme.moneyFormat) || '${{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  setError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg || '';
    this.errorEl.hidden = !msg;
  }
}

if (!customElements.get('order-grid')) {
  customElements.define('order-grid', OrderGrid);
}
